import React from "react";
import { Field, FieldProps, Form, Formik } from "collections/Form";
import i18n, { useTranslation } from "utilities/i18n";
import { DISMISS, useNotification } from "collections/Notifications";
import { LoadingIndicator, SubmitButton } from "elements";
import {
  useGetInstanceQuery,
  useUpdateInstanceMutation
} from "generated/graphql";
import { Layout } from "views/Main/Layouts";
import { InferType, object, string } from "yup";
import { needs, useGuard } from "rbac";
import BlockingForm from "collections/Form/BlockingForm";
import { FormInput } from "routes/Manage/Inventory/Settings-Shared/DeviceSettingsComponents";
import { Redirect } from "polyrouter";
import { useTenantId } from "polyrouter/SelectedTenantProvider";

// TODO: See if we can use the shared SettingsCategory from ui/glass/src/routes/Manage/Inventory/Settings-Shared/DeviceSettingsComponents.tsx
export const SettingsCategory: React.FC<{ title: string }> = props => (
  <div className="settings">
    <div className="card">
      <span className="title">{props.title}</span>
    </div>
    {props.children}
  </div>
);

const GeneralSettingsSchema = object().shape({
  name: string()
    .label(i18n.t("NAME"))
    .required(),
  productVersion: string()
    .label(i18n.t("VERSION_ALT"))
    .required(),
  serialNumber: string()
    .label(i18n.t("SERIAL_NUMBER"))
    .ensure(),
  hostName: string()
    .label(i18n.t("HOST_NAME"))
    .ensure()
});
export type GeneralSettings = InferType<typeof GeneralSettingsSchema>;

export const SettingsHeader: React.FC<{
  title: string;
  userCanEdit: boolean;
}> = ({ title, userCanEdit }) => {
  const { t } = useTranslation();
  const { Content } = Layout;

  return (
    <div className="grid">
      <div className="col-6 col-xl-8">
        <Content.Title>
          {`${t("SETTINGS")} `}
          <p className="m-0">{`| ${title}`}</p>
        </Content.Title>
      </div>
      <div className="col-6 col-xl-4 text-right">
        {userCanEdit && (
          <>
            <Field name="reset">
              {({ form: { dirty, handleReset, isSubmitting } }: FieldProps) => (
                <button
                  type="button"
                  className="btn"
                  disabled={isSubmitting || !dirty}
                  onClick={handleReset}
                >
                  {t("DISCARD")}
                </button>
              )}
            </Field>
            <Field name="submit">
              {({ form: { dirty, isSubmitting, isValid } }: FieldProps) => (
                <SubmitButton
                  isSubmitting={isSubmitting}
                  disabled={!dirty || !isValid}
                />
              )}
            </Field>
          </>
        )}
      </div>
    </div>
  );
};

export const GeneralInstanceSettings: React.FC<{
  id: string;
}> = ({ id }) => {
  const { Content } = Layout;
  const { t } = useTranslation();
  const tenantId = useTenantId();
  const [, updateInstance] = useUpdateInstanceMutation();
  const [addNotification] = useNotification();
  const userCanEdit = useGuard(needs("license:WriteTenant"));

  const { data, fetching, executor: refresh } = useGetInstanceQuery({
    variables: {
      recordId: id
    }
  });

  if (fetching) {
    return <LoadingIndicator />;
  }

  const instance = data?.getInstance;

  if (tenantId) {
    if (
      !instance ||
      id !== instance.recordId ||
      tenantId !== instance.tenantId
    ) {
      return <Redirect to="/manage/licensing/clariti/purchased-products" />;
    }

    const enableSettings = instance?.useAlternateLicencing ?? false;
    if (!enableSettings) {
      return <Redirect to="../" />;
    }
  }

  const initialValues = {
    name: instance?.niceName || "",
    productVersion: instance?.productVersion || "",
    serialNumber: instance?.serialNumber || "",
    hostName: instance?.hostname || ""
  };

  const handleSubmit = async (values: GeneralSettings) => {
    const instanceResponse = await updateInstance({
      recordId: id,
      niceName: values.name,
      version: values.productVersion,
      serialNumber: values.serialNumber,
      hostName: values.hostName
    });
    if (instanceResponse.data?.updateInstance) {
      addNotification({
        type: "success",
        message: t("SUCCESSFULLY_UPDATED_INSTANCE"),
        dismiss: DISMISS.DEFAULT
      });
      refresh({ requestPolicy: "network-only" });
    }
  };

  return (
    <Content className="settings">
      <Formik
        name="InstanceGeneralSettings"
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={GeneralSettingsSchema}
        enableReinitialize
      >
        {({ dirty }) => {
          return (
            <BlockingForm unsavedChanges={dirty}>
              <Form className="form grid">
                <SettingsHeader
                  title={initialValues.name}
                  userCanEdit={userCanEdit}
                />
                <SettingsCategory title={t("GENERAL")}>
                  <FormInput
                    label="NAME"
                    name="name"
                    tabIndex={0}
                    disabled={!userCanEdit}
                  />
                  <FormInput
                    label="VERSION_ALT"
                    name="productVersion"
                    tabIndex={1}
                    disabled={!userCanEdit}
                  />
                  <FormInput
                    label="SERIAL_NUMBER_OPTIONAL"
                    name="serialNumber"
                    tabIndex={2}
                    disabled={!userCanEdit}
                  />
                  <FormInput
                    label="HOST_NAME_OPTIONAL"
                    name="hostName"
                    tabIndex={3}
                    disabled={!userCanEdit}
                  />
                </SettingsCategory>
              </Form>
            </BlockingForm>
          );
        }}
      </Formik>
    </Content>
  );
};
